import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { PageHeader } from "@/components/ui-kit";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtTime } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useClassOptions, ClassOptionItems } from "@/components/ClassOptions";
import { PackageCheck, Undo2, Loader2, Phone, Download } from "lucide-react";

function KitRow({ s, status, busy, onMark }) {
  return (
    <div data-testid={`kit-row-${s.id}`} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm transition-colors hover:bg-violet-50/40">
      <div className="flex-1 min-w-[180px]">
        <div className="font-semibold text-slate-900">{s.name}</div>
        <div className="text-xs text-slate-500">{s.admission_number}{s.indent_id ? ` · Indent ${s.indent_id}` : ""}</div>
      </div>
      <span className="w-24 text-slate-700">{s.class_name} - {s.section}</span>
      <span className="w-20 text-slate-600">{s.uniform_size ? `Size ${s.uniform_size}` : "—"}</span>
      <span className="w-40 text-slate-600 truncate flex items-center gap-1">{s.parent_phone && <Phone size={12} />}{s.father_name || s.parent_phone || ""}</span>
      {status === "pending" ? (
        <Button data-testid={`kit-give-${s.id}`} size="sm" className="btn-brand" disabled={busy} onClick={() => onMark(s, true)}>
          {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <PackageCheck size={14} className="mr-1" />} Mark given
        </Button>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{s.kit_given_at ? `${fmtTime(s.kit_given_at)} · ${s.kit_given_by}` : "Given"}</span>
          <Button data-testid={`kit-undo-${s.id}`} size="sm" variant="outline" disabled={busy} onClick={() => onMark(s, false)}><Undo2 size={14} className="mr-1" /> Undo</Button>
        </div>
      )}
    </div>
  );
}

function SizeSummary({ branchId }) {
  const [pendingOnly, setPendingOnly] = useState(false);
  const [byGender, setByGender] = useState(true);
  const [d, setD] = useState({ sizes: [], classes: [], total: 0 });
  useEffect(() => {
    api.get("/kits/sizes", { params: { branch_id: branchId === "all" ? undefined : branchId, pending_only: pendingOnly } }).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [branchId, pendingOnly]);
  const colTotal = (z) => d.classes.reduce((a, c) => a + (c.counts[z] || 0), 0);
  const g = (c, z, k) => c.split?.[z]?.[k] || 0;
  const colG = (z, k) => d.classes.reduce((a, c) => a + g(c, z, k), 0);
  const SplitCell = ({ b, gi, u }) => (
    <div className="text-[11px] leading-tight mt-0.5"><span className="text-sky-700">B {b}</span> · <span className="text-pink-700">G {gi}</span>{u ? <span className="text-slate-400"> · ? {u}</span> : null}</div>
  );
  function download() {
    const head = byGender ? d.sizes.flatMap((z) => [`${z} Boys`, `${z} Girls`, `${z} Unknown`]) : d.sizes;
    const cells = (c) => byGender ? d.sizes.flatMap((z) => [g(c, z, "boys"), g(c, z, "girls"), g(c, z, "unknown")]) : d.sizes.map((z) => c.counts[z] || 0);
    const totals = byGender ? d.sizes.flatMap((z) => [colG(z, "boys"), colG(z, "girls"), colG(z, "unknown")]) : d.sizes.map(colTotal);
    const lines = [["Class", ...head, "Total"], ...d.classes.map((c) => [c.class_name, ...cells(c), c.total]), ["Total", ...totals, d.total]];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([lines.map((l) => l.join(",")).join("\n")], { type: "text/csv" }));
    a.download = "uniform-size-summary.csv"; a.click();
  }
  return (
    <div data-testid="size-summary" className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-slate-600 flex items-center gap-2"><input data-testid="size-pending-only" type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} className="accent-violet-600" /> Only students still waiting for their kit</label>
        <label className="text-sm text-slate-600 flex items-center gap-2"><input data-testid="size-by-gender" type="checkbox" checked={byGender} onChange={(e) => setByGender(e.target.checked)} className="accent-violet-600" /> Split into boys and girls</label>
        <span className="flex-1" />
        <Button data-testid="size-download" variant="outline" size="sm" onClick={download} disabled={!d.classes.length}><Download size={14} className="mr-1" /> Download for ordering</Button>
      </div>
      {d.classes.length === 0 ? <p className="text-sm text-slate-500">No students found.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100"><th className="py-2 pr-3 min-w-[180px]">Class</th>{d.sizes.map((z) => <th key={z} className="py-2 px-3 text-center whitespace-nowrap">{z}</th>)}<th className="py-2 px-3 text-center">Total</th></tr></thead>
            <tbody>
              {d.classes.map((c) => (
                <tr key={c.class_name} data-testid={`size-row-${c.class_name}`} className="border-b border-slate-50">
                  <td className="py-2 pr-3 font-semibold text-slate-800">{c.class_name}</td>
                  {d.sizes.map((z) => <td key={z} className={`py-2 px-3 text-center ${c.counts[z] ? "text-slate-900" : "text-slate-300"}`}>{c.counts[z] || 0}{byGender && c.counts[z] ? <SplitCell b={g(c, z, "boys")} gi={g(c, z, "girls")} u={g(c, z, "unknown")} /> : null}</td>)}
                  <td className="py-2 px-3 text-center font-semibold">{c.total}</td>
                </tr>
              ))}
              <tr data-testid="size-total-row" className="bg-violet-50 font-bold text-violet-900"><td className="py-2 pr-3">Total</td>{d.sizes.map((z) => <td key={z} className="py-2 px-3 text-center">{colTotal(z)}{byGender ? <SplitCell b={colG(z, "boys")} gi={colG(z, "girls")} u={colG(z, "unknown")} /> : null}</td>)}<td className="py-2 px-3 text-center">{d.total}</td></tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function KitsPage() {
  const { user } = useAuth();
  const isAdmin = ["master_admin", "super_admin"].includes(user?.role);
  const [status, setStatus] = useState("pending");
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("all");
  const [cls, setCls] = useState("all");
  const [q, setQ] = useState("");
  const [d, setD] = useState({ items: [], total_students: 0 });
  const [busy, setBusy] = useState(null);
  const classOpts = useClassOptions(branchId);

  const load = () => api.get("/kits", { params: { status, branch_id: branchId === "all" ? undefined : branchId, class_name: cls === "all" ? undefined : cls, search: q || undefined } })
    .then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { if (isAdmin) { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); } }, [isAdmin]);
  useEffect(() => { if (status !== "sizes") load(); }, [status, branchId, cls, q]); // eslint-disable-line react-hooks/exhaustive-deps

  async function mark(s, given) {
    setBusy(s.id);
    try {
      await api.post(`/kits/${s.id}`, { given });
      toast.success(given ? `Kit marked as given to ${s.name}` : `${s.name} moved back to pending`);
      setD((x) => ({ ...x, items: x.items.filter((i) => i.id !== s.id) }));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }

  return (
    <AppShell>
      <PageHeader eyebrow="Students" title="Kit Distribution" subtitle="Students who still need their kit, with one tap to mark it as given." />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[["pending", "Pending"], ["given", "Given"], ["sizes", "Uniform sizes"]].map(([k, l]) => (
          <button key={k} data-testid={`kit-tab-${k}`} onClick={() => setStatus(k)}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${status === k ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>{l}</button>
        ))}
        {status !== "sizes" && <span data-testid="kit-count" className="text-sm text-slate-600 ml-2">{d.items.length} {status === "pending" ? "pending" : "given"} of {d.total_students} students</span>}
        <span className="flex-1" />
        {isAdmin && (
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger data-testid="kit-branch" className="w-52 bg-white"><SelectValue placeholder="All branches" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {status !== "sizes" && <>
        <Select value={cls} onValueChange={setCls}>
          <SelectTrigger data-testid="kit-class" className="w-40 bg-white"><SelectValue placeholder="All classes" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All classes</SelectItem><ClassOptionItems opts={classOpts} /></SelectContent>
        </Select>
        <Input data-testid="kit-search" placeholder="Search name or Student ID" value={q} onChange={(e) => setQ(e.target.value)} className="w-60 bg-white" />
        </>}
      </div>
      {status === "sizes" ? <SizeSummary branchId={branchId} /> : <div data-testid="kit-list" className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100">
        {d.items.length === 0 && <p className="p-6 text-sm text-slate-500">{status === "pending" ? "Every student has their kit." : "No kits marked as given yet."}</p>}
        {d.items.map((s) => <KitRow key={s.id} s={s} status={status} busy={busy === s.id} onMark={mark} />)}
      </div>}
    </AppShell>
  );
}
